import { useMemo } from 'react';

export interface UseUniqueValuesOptions<T> {
  data: T[];
  columns: string[];
  getValue?: (item: T, column: string) => string;
}

export interface UseUniqueValuesReturn {
  uniqueValues: Record<string, string[]>;
}

/**
 * Hook for calculating unique values for filterable columns in Spreadsheet
 * Used to populate filter dropdowns with available options
 * 
 * @param options - Data, columns to extract values from, and optional value getter
 * @returns Object mapping column names to arrays of unique values
 * 
 * @example
 * // Simple usage - extracts values directly from data
 * const { uniqueValues } = useUniqueValues({
 *   data: users,
 *   columns: ['role', 'isAdmin', 'isMockUser']
 * });
 * 
 * @example
 * // With custom value getter for complex fields
 * const { uniqueValues } = useUniqueValues({
 *   data: places,
 *   columns: ['type', 'status', 'neighborhood'],
 *   getValue: (item, column) => {
 *     if (column === 'neighborhood') {
 *       return typeof item.neighborhood === 'object' 
 *         ? item.neighborhood?.name || 'None'
 *         : item.neighborhood || 'None';
 *     }
 *     return String(item[column as keyof typeof item] || 'none');
 *   }
 * });
 */
export const useUniqueValues = <T extends Record<string, any>>({
  data,
  columns,
  getValue,
}: UseUniqueValuesOptions<T>): UseUniqueValuesReturn => {
  const uniqueValues = useMemo(() => {
    const values: Record<string, string[]> = {};

    // Initialize arrays for each column
    columns.forEach(column => {
      values[column] = [];
    });

    // Extract unique values from data
    data.forEach((item) => {
      columns.forEach(column => {
        let value: string;
        
        if (getValue) {
          value = getValue(item, column);
        } else {
          const rawValue = item[column];
          
          // Handle different value types
          if (rawValue === null || rawValue === undefined) {
            value = 'None';
          } else if (typeof rawValue === 'boolean') {
            value = rawValue ? 'true' : 'false';
          } else if (typeof rawValue === 'object') {
            // For objects, try to get a meaningful string representation
            if ('name' in rawValue && rawValue.name) {
              value = String(rawValue.name);
            } else {
              value = 'None';
            }
          } else {
            value = String(rawValue);
          }
        }

        // Add to unique values if not already present
        if (value && !values[column].includes(value)) {
          values[column].push(value);
        }
      });
    });

    // Sort values for each column
    Object.keys(values).forEach(column => {
      values[column].sort();
    });

    return values;
  }, [data, columns, getValue]);

  return {
    uniqueValues,
  };
};
